import {
  IcoBook16,
  IcoCheckCircle16,
  IcoFileText16,
  IcoGlobe16,
  IcoPaint16,
  IcoRobot16,
  IcoShield16,
  IcoSparkles16,
  IcoUser16,
} from '@onefootprint/icons';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { agentBuilderUrl, investigationUrl, playbookUrl } from '@/config/constants';
import type { ResourceContext } from '../types';
import { hostOf } from './host-of';

type ResourceT = TFunction<'copilot'>;

export type ResourcePresentation = {
  href: string;
  icon: ReactNode;
  /** Bare noun for an inline reference (the user-message context chip): "Agent", "Person". */
  label: string;
  /** Display name when known (entities fall back to the fp_id), else null. Chips and the
   * created card prefer this over `label`. */
  name: string | null;
  /** A selected sub-resource, rendered by callers as a trailing breadcrumb crumb after `name`
   * (the agent builder's selected node). Absent when nothing is selected. */
  subtitle?: string | null;
  /** Creation announcement for the resource-created card: "Agent created". */
  createdTitle: string;
  /** Verb for the card's action; defaults to "Open". */
  actionLabel?: string;
  /** Short per-item lines shown under the title (Percy's suggestion descriptions). */
  summaries?: string[];
};

// `ui_block` is keyed by `key`; all other kinds carry `id`.
export const resourceKey = (r: ResourceContext): string => {
  if (r.kind === 'ui_block') {
    return r.key;
  }
  return r.id;
};

// A user message's `context` is a list of resources, but legacy messages (and history
// replayed from old sessions) carry a single object or null — normalize either to a list.
export const toResourceList = (context: ResourceContext | ResourceContext[] | null | undefined): ResourceContext[] =>
  context == null ? [] : Array.isArray(context) ? context : [context];

// The Context Explorer's folders. Entities split into people/businesses so each reads as its own
// kind; every other surfaced kind maps to one folder. The single source of truth for "does this
// resource appear in the explorer, and where" — `null` means "don't surface it" (ui_block renders
// its own inline UI, agent_suggestion shows as the aggregate card). The exhaustive switch (no
// `default`) means a new ResourceContext kind won't compile until it's placed or excluded here.
export type ResourceGroupKey =
  | 'investigation'
  | 'agent'
  | 'playbook'
  | 'person'
  | 'business'
  | 'onboarding'
  | 'artifact'
  | 'appearance';

export const resourceGroup = (resource: ResourceContext): ResourceGroupKey | null => {
  switch (resource.kind) {
    case 'investigation':
      return 'investigation';
    case 'agent':
      return 'agent';
    case 'playbook':
      return 'playbook';
    case 'entity':
      return resource.entity_kind === 'business' ? 'business' : 'person';
    case 'onboarding':
      return 'onboarding';
    case 'artifact':
      return 'artifact';
    case 'appearance':
      return 'appearance';
    case 'ui_block':
    case 'agent_suggestion':
    case 'browser_session':
      return null;
  }
};

// Folder display order. A Record (not an array) so a new ResourceGroupKey must be placed here to
// compile — keeping order, grouping, and labels in lockstep.
export const RESOURCE_GROUP_ORDER: Record<ResourceGroupKey, number> = {
  investigation: 0,
  agent: 1,
  playbook: 2,
  person: 3,
  business: 4,
  onboarding: 5,
  artifact: 6,
  appearance: 7,
};

// Route, icon, and titles for a resource — shared by the resource-created card, the user-message
// context chip, the chat-input context toggle, and the Context Explorer panel. Pass `chatSessionId`
// to thread the active chat into same-window destinations (investigation/builder routes), so opening
// a resource carries this chat along — mirroring the resource-created cards.
export const presentResource = (
  resource: ResourceContext,
  t: ResourceT,
  opts?: { chatSessionId?: string },
): ResourcePresentation | null => {
  const chatSessionId = opts?.chatSessionId;
  switch (resource.kind) {
    case 'agent':
      return {
        href: agentBuilderUrl(resource.id, { chatSessionId }),
        icon: <IcoRobot16 />,
        label: t($ => $.chat.resources.agent.label),
        name: resource.name ?? null,
        // The selected builder node is a breadcrumb crumb after the agent name, not part of it.
        subtitle: resource.selection ? (resource.selection.name ?? resource.selection.id) : null,
        createdTitle: t($ => $.chat.resources.agent.createdTitle),
      };
    case 'investigation':
      // An investigation spawned by an agent run carries an `agent_execution_id`; present it as an
      // "agent run" (robot icon, run copy, entity subject). A plain investigation shows its title.
      return resource.agent_execution_id
        ? {
            href: investigationUrl(resource.id, { chatSessionId }),
            icon: <IcoRobot16 />,
            label: t($ => $.chat.resources.agentRun.label),
            name: null,
            createdTitle: t($ => $.chat.resources.agentRun.createdTitle),
          }
        : {
            href: investigationUrl(resource.id, { chatSessionId }),
            icon: <IcoFileText16 />,
            label: t($ => $.chat.resources.investigation.label),
            name: resource.name ?? null,
            createdTitle: t($ => $.chat.resources.investigation.createdTitle),
          };
    case 'entity':
      // Entity names are sensitive vault data and rarely in scope, so fall back to the
      // fp_id (which is what the dashboard shows elsewhere).
      return resource.entity_kind === 'business'
        ? {
            href: `/businesses/${resource.id}`,
            icon: <IcoCheckCircle16 />,
            label: t($ => $.chat.resources.business.label),
            name: resource.name ?? resource.id,
            createdTitle: t($ => $.chat.resources.business.createdTitle),
          }
        : {
            href: `/users/${resource.id}`,
            icon: <IcoUser16 />,
            label: t($ => $.chat.resources.person.label),
            name: resource.name ?? resource.id,
            createdTitle: t($ => $.chat.resources.person.createdTitle),
          };
    case 'onboarding': {
      // Onboardings are viewed under the entity (no standalone route); link to the trace.
      const base = resource.entity_kind === 'business' ? 'businesses' : 'users';
      return {
        href: `/${base}/${resource.fp_id}/onboardings/${resource.id}/trace`,
        icon: <IcoShield16 />,
        label: t($ => $.chat.resources.onboarding.label),
        name: null,
        createdTitle: t($ => $.chat.resources.onboarding.createdTitle),
      };
    }
    case 'artifact':
      // Legacy events (pre-investigation_id) can't build a link, so they keep rendering nothing.
      if (!resource.investigation_id) return null;
      return {
        href: investigationUrl(resource.investigation_id, { artifactId: resource.id, chatSessionId }),
        icon: <IcoFileText16 />,
        label: t($ => $.chat.resources.artifact.label),
        name: resource.name ?? null,
        createdTitle: t($ => $.chat.resources.artifact.createdTitle),
      };
    case 'playbook':
      return {
        href: playbookUrl(resource.id, {
          editSessionId: resource.edit_session_id,
          version: resource.version_id,
          chatSessionId,
        }),
        icon: <IcoBook16 />,
        label: t($ => $.chat.resources.playbook.label),
        name: resource.name ?? null,
        subtitle: resource.selection ? (resource.selection.name ?? resource.selection.id) : null,
        createdTitle: t($ => $.chat.resources.playbook.createdTitle),
      };
    case 'appearance':
      return {
        href: `/developers/appearance/${resource.id}`,
        icon: <IcoPaint16 />,
        label: t($ => $.chat.resources.appearance.label),
        name: resource.name ?? null,
        createdTitle: t($ => $.chat.resources.appearance.createdTitle),
      };
    case 'browser_session':
      if (!resource.current_url.startsWith('http')) return null;
      return {
        href: resource.current_url,
        icon: <IcoGlobe16 />,
        label: t($ => $.chat.resources.browserSession.label),
        name: hostOf(resource.current_url),
        createdTitle: t($ => $.chat.resources.browserSession.createdTitle),
      };
    case 'ui_block':
    case 'agent_suggestion':
      return null;
  }
};

// The aggregate "Percy proposed N changes" card, built from a batch (a single suggestion has no count).
export const presentAgentSuggestions = (
  agentId: string,
  count: number,
  summaries: string[],
  t: ResourceT,
): ResourcePresentation => {
  return {
    href: agentBuilderUrl(agentId, { tab: 'changes' }),
    icon: <IcoSparkles16 />,
    label: t($ => $.chat.resources.agentSuggestions.label),
    name: null,
    createdTitle:
      count === 1
        ? t($ => $.chat.resources.agentSuggestions.createdTitle_one, { count: 1 })
        : t($ => $.chat.resources.agentSuggestions.createdTitle_other, { count }),
    actionLabel: t($ => $.chat.resources.agentSuggestions.actionLabel),
    summaries,
  };
};
